<template>
  <v-container>
    <div class="my-4">
      <h1>{{ componentName }}</h1>
    </div>

    <template v-if="parsedCode">
      <section
        v-if="parsedCode.description"
        id="description"
      >
        <markdown :source="parsedCode.description" />
      </section>

      <section id="examples">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Examples
            </h3>
          </v-card-title>
          <v-card-text>
            <examples
              :component-name="componentName"
              :inline-examples="inlineExamples"
            />
          </v-card-text>
        </v-card>
      </section>

      <v-alert
        v-if="keywords.vuetify"
        type="info"
        outlined
      >
        This component is a wrapper for the <code>{{ keywords.vuetify }}</code> Vuetify component.
        For more information, see its
        <a
          :href="`https://vuetifyjs.com/en/api/${keywords.vuetify}/`"
          target="_blank"
          rel="noreferrer noopener"
        >Vuetify Documentation</a>
      </v-alert>

      <section id="props">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Props
            </h3>
          </v-card-title>
          <v-card-text>
            <props-table :data="parsedCode.props" />
          </v-card-text>
        </v-card>
      </section>

      <section id="slots">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Slots
            </h3>
          </v-card-title>
          <v-card-text>
            <slots-table :data="parsedCode.slots" />
          </v-card-text>
        </v-card>
      </section>

      <section id="data">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Data
            </h3>
          </v-card-title>
          <v-card-text>
            <data-table :data="parsedCode.data" />
          </v-card-text>
        </v-card>
      </section>

      <section id="computed">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Computed
            </h3>
          </v-card-title>
          <v-card-text>
            <computed-table :data="parsedCode.computed" />
          </v-card-text>
        </v-card>
      </section>

      <section id="methods">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Methods
            </h3>
          </v-card-title>
          <v-card-text>
            <methods-table :data="parsedCode.methods" />
          </v-card-text>
        </v-card>
      </section>

      <section id="events">
        <v-card class="my-10">
          <v-card-title>
            <h3 class="heading">
              Events
            </h3>
          </v-card-title>
          <v-card-text>
            <events-table :data="parsedCode.events" />
          </v-card-text>
        </v-card>
      </section>
    </template>
  </v-container>
</template>

<script>
import Examples from '@/components/componentPreview/Examples.vue';
import PropsTable from '@/components/componentPreview/PropsTable.vue';
import SlotsTable from '@/components/componentPreview/SlotsTable.vue';
import DataTable from '@/components/componentPreview/DataTable.vue';
import ComputedTable from '@/components/componentPreview/ComputedTable.vue';
import MethodsTable from '@/components/componentPreview/MethodsTable.vue';
import EventsTable from '@/components/componentPreview/EventsTable.vue';
import {parseVueCode} from '@/helpers/parseSourceCode';
import Markdown from '@/components/Markdown';

export default {
  name: 'ComponentPreview',
  components: {
    Markdown,
    Examples,
    EventsTable,
    MethodsTable,
    ComputedTable,
    DataTable,
    SlotsTable,
    PropsTable,
  },
  props: {
    componentName: String,
  },
  asyncComputed: {
    sourceCode() {
      // eslint-disable-next-line prefer-template
      return import('!raw-loader!@resman/ui/components/' + this.componentName + '/' + this.componentName + '.vue').then((c) => c.default);
    },
    async parsedCode() {
      if (!this.sourceCode) return null;

      return parseVueCode(this.sourceCode);
    },
  },
  computed: {
    inlineExamples() {
      if (!this.parsedCode) return [];

      return this.parsedCode.keywords.filter(({name}) => name === 'example');
    },
    keywords() {
      if (!this.parsedCode) return {};

      const keywords = {};
      this.parsedCode.keywords.forEach(({name, description}) => {
        keywords[name] = description;
      });

      return keywords;
    },
  },
};
</script>

<style lang="scss" scoped>
  >>> p:last-child {
    margin: 0 !important;
  }
</style>
